import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI, teamDirectoryAPI } from '../services/api';
import { useLabDemo } from '../context/LabDemoContext';
import { AIGeneratedBadge } from '../components/LabBadges';
import CreateProjectModal from '../components/CreateProjectModal';
import { projects as mockProjects, teamMembers as mockTeamMembers } from '../mock';
import { 
  Plus, TrendingUp, AlertTriangle, X, Mail, Phone, MapPin, 
  Briefcase, FolderKanban, ChevronRight, Target, Users, Clock,
  AlertCircle, FlaskConical
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';

// ================================================
// TEAM PROFILE SIDE PANEL
// ================================================
const TeamProfilePanel = ({ member, onClose }) => {
  if (!member) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="team-profile-panel">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      
      <div 
        className="relative w-full max-w-sm shadow-2xl overflow-y-auto animate-slide-in-right"
        style={{ backgroundColor: DESIGN.colors.card }}
      >
        <div 
          className="sticky top-0 border-b p-6"
          style={{ backgroundColor: DESIGN.colors.card, borderColor: DESIGN.colors.border }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
              >
                {member.avatar}
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                  {member.name}
                </h2>
                {member.title && (
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{member.title}</p>
                )}
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 rounded-xl transition-colors hover:bg-gray-100"
            >
              <X className="w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {member.is_owner && (
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
              style={{ 
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
                color: '#D97706',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              Project Manager
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
              Contact
            </h3>
            
            {[
              { icon: Mail, value: member.email },
              { icon: Phone, value: member.phone },
              { icon: MapPin, value: member.city ? `${member.city}${member.state ? `, ${member.state}` : ''}` : null }
            ].filter(item => item.value).map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: DESIGN.colors.background }}
                >
                  <item.icon className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
                </div>
                <span className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>{item.value}</span>
              </div>
            ))}
          </div>

          {member.department && (
            <div className="space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
                Department
              </h3>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: DESIGN.colors.background }}
                >
                  <Briefcase className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
                </div>
                <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>{member.department}</span>
              </div>
            </div>
          )}

          {member.task_count !== undefined && (
            <div 
              className="p-5 rounded-2xl"
              style={{ 
                background: `linear-gradient(135deg, ${DESIGN.colors.primarySubtle} 0%, ${DESIGN.colors.background} 100%)`,
                border: `1px solid ${DESIGN.colors.border}`
              }}
            >
              <div className="text-4xl font-black" style={{ color: DESIGN.colors.primary }}>
                {member.task_count}
              </div>
              <div className="text-sm mt-1" style={{ color: DESIGN.colors.textMuted }}>Active Tasks</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ================================================
// AVATAR CLUSTER WITH EXPAND ON HOVER
// ================================================
const AvatarCluster = ({ contributors, maxShow = 4, onMemberClick }) => {
  const displayContributors = contributors?.slice(0, maxShow) || [];
  const extraCount = (contributors?.length || 0) - maxShow;

  if (!displayContributors.length) return null;

  return (
    <div className="flex items-center group" data-testid="avatar-cluster">
      {displayContributors.map((contributor, idx) => (
        <div 
          key={contributor.id} 
          className={`${idx > 0 ? '-ml-3' : ''} cursor-pointer transition-all duration-300 group-hover:ml-0 group-hover:mr-1 hover:scale-110 hover:z-10`}
          onClick={(e) => {
            e.stopPropagation();
            onMemberClick(contributor);
          }}
          title={contributor.name}
        >
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-white shadow-md transition-transform"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary}40 0%, ${DESIGN.colors.primary}20 100%)`,
              color: DESIGN.colors.textSecondary 
            }}
          >
            {contributor.avatar}
          </div>
        </div>
      ))}
      
      {extraCount > 0 && (
        <div 
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-white shadow-md -ml-3 group-hover:ml-0"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.border} 0%, ${DESIGN.colors.background} 100%)`,
            color: DESIGN.colors.textSecondary 
          }}
        >
          +{extraCount}
        </div>
      )}
    </div>
  );
};

// ================================================
// PREMIUM PROJECT CARD
// ================================================
const PremiumProjectCard = ({ project, onNavigate, onMemberClick, index }) => {
  const isOnTrack = project.status === 'on-track';
  const statusConfig = isOnTrack 
    ? { color: '#00C9A7', bg: 'rgba(0, 201, 167, 0.08)', label: 'On Track', icon: TrendingUp }
    : { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.08)', label: 'Focus Required', icon: AlertTriangle };
  
  const contributors = (project.contributors || []).filter(c => c.id !== project.owner?.id);
  
  return (
    <div 
      onClick={onNavigate}
      className="relative rounded-2xl cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 group overflow-hidden"
      style={{
        backgroundColor: DESIGN.colors.card,
        borderLeft: `5px solid ${statusConfig.color}`,
        boxShadow: DESIGN.shadows.lg,
        animationDelay: `${index * 100}ms`,
        animation: 'fadeInUp 0.5s ease-out forwards',
        opacity: 0
      }}
      data-testid={`project-card-${project.id}`}
    >
      {/* Hover gradient overlay */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(135deg, ${statusConfig.bg} 0%, transparent 60%)`
        }}
      />
      
      <div className="relative p-6">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 
                className="text-xl font-bold truncate"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                {project.name}
              </h3>
              {/* AI Generated Badge for LAB-created projects */}
              {project.isLabGenerated && (
                <AIGeneratedBadge size="sm" />
              )}
              <div 
                className="flex items-center gap-2 px-3 py-1.5 rounded-full flex-shrink-0"
                style={{ 
                  backgroundColor: statusConfig.bg,
                  boxShadow: `0 0 15px ${statusConfig.color}30`
                }}
              >
                <statusConfig.icon className="w-3.5 h-3.5" style={{ color: statusConfig.color }} />
                <span className="text-xs font-bold" style={{ color: statusConfig.color }}>
                  {statusConfig.label}
                </span>
              </div>
            </div>
            <p 
              className="text-sm line-clamp-2"
              style={{ color: DESIGN.colors.textSecondary }}
            >
              {project.description}
            </p>
            {/* LAB Origin Link */}
            {project.isLabGenerated && (
              <div className="flex items-center gap-2 mt-2">
                <FlaskConical className="w-3 h-3" style={{ color: DESIGN.colors.primary }} />
                <span className="text-xs" style={{ color: DESIGN.colors.primary }}>
                  Created from LAB • {project.labConversionDate}
                </span>
              </div>
            )}
          </div>
          
          {/* Avatar Cluster */}
          <div className="ml-4">
            <AvatarCluster 
              contributors={contributors}
              maxShow={4}
              onMemberClick={(member) => onMemberClick(member, false)}
            />
          </div>
        </div>

        {/* Project Manager */}
        {project.owner && (
          <div 
            className="flex items-center gap-3 mb-5 p-3 rounded-xl cursor-pointer transition-all hover:shadow-md"
            style={{ backgroundColor: DESIGN.colors.background }}
            onClick={(e) => {
              e.stopPropagation();
              onMemberClick(project.owner, true);
            }}
          >
            <div className="relative">
              <div 
                className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
              >
                {project.owner.avatar}
              </div>
              <div 
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ 
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  boxShadow: '0 2px 6px rgba(245, 158, 11, 0.4)'
                }}
              >
                <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                {project.owner.name}
              </p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Project Manager</p>
            </div>
          </div>
        )}

        {/* Progress Section */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
              Progress
            </span>
            <span 
              className="text-lg font-black"
              style={{ color: statusConfig.color }}
            >
              {project.completion_percentage}%
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
            <div 
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${project.completion_percentage}%`,
                background: `linear-gradient(90deg, ${statusConfig.color}CC 0%, ${statusConfig.color} 100%)`,
                boxShadow: `0 0 15px ${statusConfig.color}60`
              }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Active Tasks', value: project.active_tasks, alert: false },
            { label: 'On-Time Rate', value: `${Math.round(project.on_time_rate * 100)}%`, alert: false },
            { label: 'Overdue Steps', value: project.overdue_steps, alert: project.overdue_steps > 0 },
            { label: 'Overdue Phases', value: project.overdue_phases || 0, alert: (project.overdue_phases || 0) > 0 }
          ].map((stat, idx) => (
            <div 
              key={idx}
              className={`p-3 rounded-xl text-center transition-all ${stat.alert ? 'animate-pulse-subtle' : ''}`}
              style={{ 
                backgroundColor: stat.alert ? 'rgba(239, 68, 68, 0.08)' : DESIGN.colors.background,
                border: stat.alert ? '1px solid rgba(239, 68, 68, 0.2)' : 'none'
              }}
            >
              <p 
                className="text-xl font-black"
                style={{ 
                  color: stat.alert ? DESIGN.colors.error : DESIGN.colors.textPrimary 
                }}
              >
                {stat.value}
              </p>
              <p className="text-[10px] font-medium uppercase tracking-wider mt-1" style={{ color: DESIGN.colors.textMuted }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* View Details CTA */}
        <div 
          className="flex items-center justify-end gap-1 mt-4 pt-4 border-t opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ borderColor: DESIGN.colors.borderSubtle }}
        >
          <span className="text-sm font-semibold" style={{ color: statusConfig.color }}>
            View Details
          </span>
          <ChevronRight className="w-4 h-4" style={{ color: statusConfig.color }} />
        </div>
      </div>
    </div>
  );
};

// ================================================
// MAIN PROJECTS PAGE
// ================================================
const Projects = () => {
  const navigate = useNavigate();
  const { labProject } = useLabDemo();
  const [filter, setFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchData = async () => {
    try {
      const [projectsRes, teamRes] = await Promise.all([
        projectAPI.getAll(),
        teamDirectoryAPI.getAll().catch(() => ({ data: { members: [] } }))
      ]);
      const fetchedProjects = projectsRes.data.projects || [];
      const fetchedTeam = teamRes.data.members || [];
      
      // Use API data if available, otherwise fall back to mock data
      setProjects(fetchedProjects.length > 0 ? fetchedProjects : mockProjects);
      setTeamMembers(fetchedTeam.length > 0 ? fetchedTeam : mockTeamMembers);
    } catch (error) {
      console.error('Error fetching projects, using mock data:', error);
      // Use mock data as fallback
      setProjects(mockProjects);
      setTeamMembers(mockTeamMembers);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Merge LAB-created project with API projects (LAB project at top if exists)
  const allProjects = useMemo(() => {
    if (labProject) {
      // Remove any existing LAB project to avoid duplicates, then add fresh one at top
      const filteredApiProjects = projects.filter(p => p.id !== labProject.id);
      return [labProject, ...filteredApiProjects];
    }
    return projects;
  }, [projects, labProject]);

  const filteredProjects = allProjects.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'on-track') return p.status === 'on-track';
    if (filter === 'focus-required') return p.status === 'focus-required';
    return true;
  });

  const handleMemberClick = (member, isOwner = false) => {
    const fullMember = teamMembers.find(m => m._id === member.id || m.id === member.id);
    
    if (fullMember) {
      setSelectedMember({
        id: fullMember._id || fullMember.id,
        name: fullMember.displayName || `${fullMember.firstName} ${fullMember.lastName}`,
        avatar: member.avatar,
        title: fullMember.roles?.[0]?.title || fullMember.title,
        email: fullMember.email,
        phone: fullMember.phone,
        city: fullMember.city,
        state: fullMember.state,
        department: fullMember.roles?.[0]?.department || fullMember.department,
        is_owner: isOwner
      });
    } else {
      setSelectedMember({ ...member, is_owner: isOwner });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div 
              className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
              style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }}
            />
          </div>
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>Loading projects...</p>
        </div>
      </div>
    );
  }

  const filters = [
    { key: 'all', label: 'All Projects', count: allProjects.length },
    { key: 'on-track', label: 'On Track', count: allProjects.filter(p => p.status === 'on-track').length },
    { key: 'focus-required', label: 'Focus Required', count: allProjects.filter(p => p.status === 'focus-required').length }
  ];

  return (
    <div className="space-y-8" data-testid="projects-page">
      {/* Premium Page Header */}
      <div className="relative">
        {/* Animated gradient background */}
        <div 
          className="absolute inset-0 rounded-3xl opacity-50"
          style={{
            background: 'linear-gradient(135deg, rgba(0, 201, 167, 0.05) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(59, 130, 246, 0.05) 100%)',
            animation: 'gradientShift 8s ease infinite'
          }}
        />
        
        <div className="relative flex items-center justify-between py-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  boxShadow: DESIGN.shadows.glowPrimary
                }}
              >
                <FolderKanban className="w-5 h-5 text-white" />
              </div>
              <h1 
                className="text-3xl font-black tracking-tight"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                PROJECTS
              </h1>
            </div>
            <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
              Strategic portfolio management and outcome tracking
            </p>
          </div>
          
          <button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              color: 'white',
              boxShadow: DESIGN.shadows.glowPrimary
            }}
            data-testid="new-project-btn"
          >
            <Plus className="w-5 h-5" />
            New Project
          </button>
        </div>
      </div>

      {/* Premium Filter Tabs */}
      <div 
        className="inline-flex items-center gap-1 p-1.5 rounded-2xl"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        {filters.map(({ key, label, count }) => (
          <button 
            key={key}
            onClick={() => setFilter(key)} 
            className="relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300"
            style={{ 
              backgroundColor: filter === key ? DESIGN.colors.primary : 'transparent',
              color: filter === key ? 'white' : DESIGN.colors.textSecondary,
              boxShadow: filter === key ? DESIGN.shadows.glowPrimary : 'none'
            }}
          >
            {label}
            <span 
              className="ml-2 px-2 py-0.5 rounded-full text-xs"
              style={{ 
                backgroundColor: filter === key ? 'rgba(255,255,255,0.2)' : DESIGN.colors.border,
                color: filter === key ? 'white' : DESIGN.colors.textMuted
              }}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredProjects.map((project, index) => (
          <PremiumProjectCard 
            key={project.id}
            project={project}
            index={index}
            onNavigate={() => navigate(`/projects/${project.id}`)}
            onMemberClick={handleMemberClick}
          />
        ))}
      </div>

      {/* Team Profile Side Panel */}
      {selectedMember && (
        <TeamProfilePanel 
          member={selectedMember} 
          onClose={() => setSelectedMember(null)} 
        />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes gradientShift {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Create Project Modal */}
      <CreateProjectModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchData(); // Refresh projects list
        }}
      />
    </div>
  );
};

export default Projects;
